<script lang="ts" setup>
import { Candidate } from '@/core/collections/contracts-api/candidates/candidate.interface';
import { AddressTypeKey, ApiResource, CANDIDATE_ENDPOINTS } from '@nxtpeople/nxt-styles';
import { AxiosRequestConfig } from 'axios';
import { PropType } from 'vue';
import Dropdown from './Dropdown.vue';

//#region Props & Emits
const emits = defineEmits(['update:candidateSyncId', 'update:candidate']);

defineProps({
   name: { type: String },
   candidateSyncId: { type: [String, Number], default: null },
   placeholder: { type: String, default: null },
   label: { type: String, default: null },
   options: { type: Array as PropType<Array<Candidate>>, default: () => [] },
   initialDataCallback: { type: Function, default: null },
   errors: { type: Object, default: () => ({}) },
   disabled: { type: Boolean, default: false },
});
//#endregion

//#region ModelValue
function changeCandidate(candidate?: Candidate) {
   emits('update:candidate', candidate);
   emits('update:candidateSyncId', candidate?.sync_id);
}
//#endregion

//#region Options
const meta = ref<any>(null);
async function fetchCandidates(request: AxiosRequestConfig<any>): Promise<Array<Candidate>> {
   const response = await http.get<ApiResource<Array<Candidate>>>(CANDIDATE_ENDPOINTS.GET_ALL(), {
      ...request,
      params: { ...request.params, 'include[]': ['addresses.city', 'employees'] },
   });

   meta.value = response.data?.meta ?? null;
   return response.data?.data ?? [];
}

//#endregion
</script>

<template>
   <Dropdown
      class="candidate-dropdown"
      :name="name ?? 'candidate_sync_id'"
      :placeholder="placeholder"
      :model-value="candidateSyncId"
      :search-type="'custom'"
      :search-method="fetchCandidates"
      :options="options"
      :emit-object="true"
      :label="label"
      :unique-key="'sync_id'"
      :display-key="'first_name'"
      :clear-search-on-select="true"
      :has-clear-option="true"
      :initial-data-callback="initialDataCallback"
      :errors="errors"
      :disabled="disabled"
      :load-more-meta="meta"
      :load-more-type="'scroll'"
      @update:model-value="changeCandidate"
   >
      <template #displayOption="{ option }: { option: Candidate }">
         <slot name="displayOption" v-bind="{ option }">
            <p class="m-0">{{ option?.first_name }} {{ option?.last_name }}</p>
         </slot>
      </template>

      <template #option="{ option }: { option: Candidate }">
         <div class="d-flex flex-column align-items-start overflow-hidden">
            <p class="m-0 text-truncate">
               <strong>{{ option?.first_name }} {{ option?.last_name }}</strong>
               <span v-date="option?.birthdate" class="ms-2"></span>
            </p>
            <p v-if="option?.official_address" class="m-0 text-truncate">
               <span v-address="option.official_address"></span>
            </p>
            <p v-else-if="option?.addresses" class="m-0 text-truncate">
               <span v-address="option?.addresses?.find((a) => a?.address_type?.key === AddressTypeKey.Official)"></span>
            </p>
         </div>
      </template>
   </Dropdown>
</template>
